import React, { useState } from 'react';
import { AppointmentTimeFilterEnum } from '../Enum/AppointmentTimeFilterEnum';
import { AppointmentTimeFrameEnum } from '../Enum/AppointmentTimeFrameEnum';
import { RequestDayFilterEnum } from '../Enum/RequestDayFilterEnum';
import { TimePeriodFilterEnum } from '../Enum/TimePeriodFilterEnum';
import { WidgetTimeFilterEnum } from '../Enum/WidgetTimeFilterEnum';
import Authorization from '../utils/Authorization';
import { CarePlanNotReviewedPeriodEnum } from '../Enum/CarePlanNotReviewedPeriodEnum';
import { RequestTimeFilterEnum } from '../Enum/RequestTimeFilterEnum';
import { TransitionalCareDateRangeEnum } from '../Enum/TransitionalCareDateRangeEnum';
import { TCPerformanceFilterRoleEnum } from '../Enum/TCPerformanceFilterRoleEnum';
import { ProgramTypeEnum } from '../Enum/ProgramTypeEnum';
import { OnboardingDashboardCardType } from '../Models/Widget/OnboardingPerformanceDashboardDetail.model';
import { SNCDashboardDrillDownType } from '../Enum/SNCDashboardDrillDownType';
import { SNCDashboardMetricDateRange } from '../Enum/SNCDashboardMetricDateRange';
import { ISNCDashboardTeam } from '../Models/SNCDashboard/SNCDashboard.model';
import { ISNCDashboardProvider } from '../Models/SNCDashboard/SNCDashboard.model';

const getPreviousMonthSameDate = () => {
    const today = new Date();
    const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastDay = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0).getDate();

    return new Date(
        previousMonth.getFullYear(),
        previousMonth.getMonth(),
        Math.min(today.getDate(), lastDay)
    );
};

const WidgetContext = React.createContext<any>(null);

export const WidgetProvider = ({ children }: { children: React.ReactNode }) => {
    const [authData, setAuthData] = React.useState<Authorization>(new Authorization());

    const [selectedProvider, setSelectedProvider] = useState('');
    const [showInActiveProviders, setShowInActiveProviders] = useState(false);

    const [engagementSelectedPlans, setEngagementSelectedPlans] = useState<string[]>(['All']);
    const [engagementDateFilter, setEngagementDateFilter] = useState(TimePeriodFilterEnum.Last6Months);
    const [engagementFromDate, setEngagementFromDate] = useState(null);
    const [engagementToDate, setEngagementToDate] = useState(null);
    const [engagementSelectedTeam, setEngagementSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [loadedEngagementTeam, setLoadedEngagementTeam] = useState(false);
    const [engagementShowInActiveTeams, setEngagementShowInActiveTeams] = useState(false);

    const [schedulingDateFilter, setSchedulingDateFilter] = useState(AppointmentTimeFrameEnum.Last7Days);
    const [schedulingFromDate, setSchedulingFromDate] = useState(null);
    const [schedulingToDate, setSchedulingToDate] = useState(null);
    const [schedulingSelectedPlans, setSchedulingSelectedPlans] = useState<string[]>(['All']);
    const [schedulingSelectedProvider, setSchedulingSelectedProvider] = useState('All');
    const [schedulingSelectedStaff, setSchedulingSelectedStaff] = useState('All');
    const [schedulingSelectedTeam, setSchedulingSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [loadedSchedulingTeam, setLoadedSchedulingTeam] = useState(false);
    const [schedulingShowInActiveProviders, setSchedulingShowInActiveProviders] = useState(false);
    const [schedulingShowInActiveTeams, setSchedulingShowInActiveTeams] = useState(false);
    const [schedulingShowInActiveStaffs, setSchedulingShowInActiveStaffs] = useState(false);

    const [requestSelectedPlan, setRequestSelectedPlan] = useState('All');
    const [requestSelectedProvider, setRequestSelectedProvider] = useState('All');
    const [requestSelectedTeam, setRequestSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [selectedRequestType, setSelectedRequestType] = useState('All');
    const [loadedRequestTeam, setLoadedRequestTeam] = useState(false);

    const [appointmentSelectedProvider, setAppointmentSelectedProvider] = useState('All');
    const [appointmentSelectedTeam, setAppointmentSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [appointmentDateFilter, setAppointmentDateFilter] = useState(TimePeriodFilterEnum.Last6Months);
    const [appointmentFromDate, setAppointmentFromDate] = useState(null);
    const [appointmentToDate, setAppointmentToDate] = useState(null);
    const [loadedAppointmentTeam, setLoadedAppointmentTeam] = useState(false);
    const [appointmentShowInActiveProviders, setAppointmentShowInActiveProviders] = useState(false);
    const [appointmentShowInActiveTeams, setAppointmentShowInActiveTeams] = useState(false);

    const [teamMemberSelectedPlans, setTeamMemberSelectedPlans] = useState<string[]>(['All']);
    const [teamMemberSelectedTeam, setTeamMemberSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [loadedTeamMemberTeam, setLoadedTeamMemberTeam] = useState(false);
    const [teamShowInActiveTeams, setTeamShowInActiveTeams] = useState(false);
    const [teamMemberFromDate, setTeamMemberFromDate] = useState(null);
    const [teamMemberToDate, setTeamMemberToDate] = useState(null);

    const [reqSelectedProvider, setReqSelectedProvider] = useState('All');
    const [reqDayFilter, setReqDayFilter] = useState(RequestDayFilterEnum.WithIn15Days);
    const [reqDateFilter, setReqDateFilter] = useState(TimePeriodFilterEnum.Last3Months);
    const [reqFromDate, setReqFromDate] = useState(null);
    const [reqToDate, setReqToDate] = useState(null);
    const [loadedReqProvider, setLoadedReqProvider] = useState(false);
    const [requestShowInActiveProviders, setRequestShowInActiveProviders] = useState(false);

    const [tcFacility, setTcFacility] = useState('All');
    const [tcAdmissionRules, setTcAdmissionRules] = useState('All');
    const [tcTeam, setTcTeam] = useState(authData.getDefaultTeamId('All'));
    const [tcDateFilter, setTcDateFilter] = useState(TransitionalCareDateRangeEnum.Today);
    const [tcFromDate, setTcFromDate] = useState(null);
    const [tcToDate, setTcToDate] = useState(null);
    const [tcShowInActiveTeams, setTcShowInActiveTeams] = useState(false);
    const [tcTab, setTcTab] = useState(1);

    const [engagementSummaryDateFilter, setEngagementSummaryDateFilter] = useState(TimePeriodFilterEnum.Last6Months);
    const [engagementSummaryProvider, setEngagementSummaryProvider] = useState('All');
    const [engagementSummaryTeam, setEngagementSummaryTeam] = useState(authData.getDefaultTeamId('All'));
    const [engagementSummaryFromDate, setEngagementSummaryFromDate] = useState(null);
    const [engagementSummaryToDate, setEngagementSummaryToDate] = useState(null);
    const [engagementSummaryShowInActiveTeams, setEngagementSummaryShowInActiveTeams] = useState(false);
    const [engagementSummaryShowInActiveProviders, setEngagementSummaryShowInActiveProviders] = useState(false);

    const [memberWithoutEncounterFilter, setMemberWithoutEncounterFilter] = useState({
        team: authData.getDefaultTeamId('All'),
        healthPlan: 'All',
        dateFilter: WidgetTimeFilterEnum.Last30Days,
        fromDate: null,
        toDate: null,
        showInActiveTeams: false,
    });

    const [undeterminedReferralDay, setUndeterminedReferralDay] = useState(1);
    const [undeterminedIncludeScheduledStatus, setUndeterminedIncludeScheduledStatus] = useState(false);

    const [inProgressProvider, setInProgressProvider] = useState('');
    const [inProgressSelectedDay, setInProgressSelectedDay] = useState('1');
    const [inProgressDateFilter, setInProgressDateFilter] = useState(WidgetTimeFilterEnum.Last30Days);
    const [inProgressFromDate, setInProgressFromDate] = useState(null);
    const [inProgressToDate, setInProgressToDate] = useState(null);
    const [inProgressShowInActiveProviders, setInProgressShowInActiveProviders] = useState(false);

    const [closedSelectedFilter, setClosedSelectedFilter] = useState('1');
    const [closedDateFilter, setClosedDateFilter] = useState(TimePeriodFilterEnum.Last3Months);
    const [closedFromDate, setClosedFromDate] = useState(null);
    const [closedToDate, setClosedToDate] = useState(null);

    const [qualityMetricsDateFilter, setQualityMetricsDateFilter] = useState(WidgetTimeFilterEnum.Last30Days);
    const [qualityMetricsFromDate, setQualityMetricsFromDate] = useState(null);
    const [qualityMetricsToDate, setQualityMetricsToDate] = useState(null);
    const [qualityMetricsTab, setQualityMetricsTab] = useState(1);

    const [exceptionTab, setExceptionTab] = useState(1);

    const [individualEngagementDateFilter, setIndividualEngagementDateFilter] = useState(TimePeriodFilterEnum.Last6Months);
    const [individualEngagementFromDate, setIndividualEngagementFromDate] = useState(null);
    const [individualEngagementToDate, setIndividualEngagementToDate] = useState(null);
    const [individualEngagementSelectedProvider, setIndividualEngagementSelectedProvider] = useState('All');
    const [individualEngagementShowInActiveProviders, setIndividualEngagementShowInActiveProviders] = useState(false);

    const [onboardCallSelectedProvider, setOnboardCallSelectedProvider] = useState(Boolean(authData.UserId) ? authData.UserId : 'All');
    const [onboardCallShowInActiveProviders, setOnboardCallShowInActiveProviders] = useState(false);
    const [onboardCallSelectedFilter, setOnboardCallSelectedFilter] = useState(TimePeriodFilterEnum.Last1Month);
    const [onboardCallFromDate, setOnboardCallFromDate] = useState(null);
    const [onboardCallToDate, setOnboardCallToDate] = useState(null);
    const [onboardCallPopulationTypes, setOnboardCallPopulationTypes] = useState([]);

    const [fuhfumVisitReadmissionDateFilter, setFuhfumVisitReadmissionDateFilter] = useState(WidgetTimeFilterEnum.Last30Days);
    const [fuhfumVisitReadmissionFromDate, setFuhfumVisitReadmissionFromDate] = useState(null);
    const [fuhfumVisitReadmissionToDate, setFuhfumVisitReadmissionToDate] = useState(null);
    const [fuhfumVisitReadmissionTab, setFuhfumVisitReadmissionTab] = useState(1);

    const [appointmentTrackingTab, setAppointmentTrackingTab] = useState(1);
    const [fullProgramAppointment, setFullProgramAppointment] = useState({
        provider: 'All',
        schedulingStaff: 'All',
        healthPlan: 'All',
        timeFilter: AppointmentTimeFilterEnum.Next30Days,
        fromDate: null,
        toDate: null,
        includeInActiveProviders: false,
        includeInActiveStaffs: false,
        prepareMembers: false,
    });
    const [hraAppointment, setHraAppointment] = useState({
        provider: 'All',
        schedulingStaff: 'All',
        healthPlan: 'All',
        timeFilter: AppointmentTimeFilterEnum.Next30Days,
        fromDate: null,
        toDate: null,
        includeInActiveProviders: false,
        includeInActiveStaffs: false,
        prepareMembers: false,
    });
    const [fuhfumAppointment, setFuhfumAppointment] = useState({
        provider: 'All',
        schedulingStaff: 'All',
        timeFilter: AppointmentTimeFilterEnum.Next30Days,
        fromDate: null,
        toDate: null,
        includeInActiveProviders: false,
        includeInActiveStaffs: false,
        prepareMembers: false,
    });

    const [fullProgramReferralDateFilter, setFullProgramReferralDateFilter] = useState(WidgetTimeFilterEnum.Last30Days);
    const [fullProgramReferralFromDate, setFullProgramReferralFromDate] = useState(null);
    const [fullProgramReferralToDate, setFullProgramReferralToDate] = useState(null);
    const [fullProgramReferralTab, setFullProgramReferralTab] = useState(1);

    const [scheduledFilterBy, setScheduledFilterBy] = useState(AppointmentTimeFilterEnum.Next30Days);
    const [scheduledSelectedTeam, setScheduledSelectedTeam] = useState('All');
    const [scheduledShowInActiveTeams, setScheduledShowInActiveTeams] = useState(false);
    const [scheduledFromDate, setScheduledFromDate] = useState(null);
    const [scheduledToDate, setScheduledToDate] = useState(null);

    const [submittedClaimSelectedProvider, setSubmittedClaimSelectedProvider] = useState('All');
    const [submittedClaimSelectedPlans, setSubmittedClaimSelectedPlans] = useState<string[]>(['All']);
    const [submittedClaimSelectedTeam, setSubmittedClaimSelectedTeam] = useState('All');
    const [submittedClaimShowInActiveTeams, setSubmittedClaimShowInActiveTeams] = useState(false);
    const [submittedClaimShowInActiveProviders, setSubmittedClaimShowInActiveProviders] = useState(false);

    const [inpersonVisitProvider, setInpersonVisitProvider] = useState('');
    const [inpersonVisitYear, setInpersonVisitYear] = useState('2023');
    const [inpersonVisitMonth, setInpersonVisitMonth] = useState('1');
    const [inpersonVisitShowInActiveProviders, setInpersonVisitShowInActiveProviders] = useState(false);

    const [surveySelectedProvider, setSurveySelectedProvider] = useState('All');
    const [surveySelectedFilter, setSurveySelectedFilter] = useState(2);
    const [surveyFromDate, setSurveyFromDate] = useState(null);
    const [surveyToDate, setSurveyToDate] = useState(null);
    const [surveyShowInActiveProviders, setSurveyShowInActiveProviders] = useState(false);

    const [schedulerSelectedProvider, setSchedulerSelectedProvider] = useState('');
    const [schedulerDateFilter, setSchedulerDateFilter] = useState(WidgetTimeFilterEnum.Last30Days);
    const [schedulerFromDate, setSchedulerFromDate] = useState(null);
    const [schedulerToDate, setSchedulerToDate] = useState(null);
    const [schedulerShowInActiveProviders, setSchedulerShowInActiveProviders] = useState(false);

    const [carePlanSelectedTeam, setCarePlanSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [carePlanDateFilter, setCarePlanDateFilter] = useState(CarePlanNotReviewedPeriodEnum.Last90Days);
    const [carePlanDate, setCarePlanDate] = useState<Date | null>(null);
    const [carePlanShowInActiveTeams, setCarePlanShowInActiveTeams] = useState(false);

    const [memberPromisesSelectedProvider, setMemberPromisesSelectedProvider] = useState(Boolean(authData.UserId) ? authData.UserId : 'All');
    const [memberPromisesShowInActiveProviders, setMemberPromisesShowInActiveProviders] = useState(false);
    const [memberPromisesSelectedTeam, setMemberPromisesSelectedTeam] = useState('NotSelected');
    const [memberPromisesShowInActiveTeams, setMemberPromisesShowInActiveTeams] = useState(false);
    const [memberPromisesRequestType, setMemberPromisesRequestType] = useState('0');
    const [memberPromisesFilterBy, setMemberPromisesFilterBy] = useState(RequestTimeFilterEnum.From0To30Days);
    const [memberPromisesFromDate, setMemberPromisesFromDate] = useState(null);
    const [memberPromisesToDate, setMemberPromisesToDate] = useState(null);
    const [memberPromisesTab, setMemberPromisesTab] = useState(1);

    const [documentTimePeriod, setDocumentTimePeriod] = useState(3);
    const [documentSelectedTeam, setDocumentSelectedTeam] = useState(authData.getDefaultTeamId('All'));
    const [documentShowInActiveTeams, setDocumentShowInActiveTeams] = useState(false);

    const [tcPerformanceTeam, setTcPerformanceTeam] = useState(authData.getDefaultTeamId('All'));
    const [tcPerformanceFilterRole, setTcPerformanceFilterRole] = useState(TCPerformanceFilterRoleEnum.LicensedStaff);
    const [tcPerformanceDateFilter, setTcPerformanceDateFilter] = useState(TransitionalCareDateRangeEnum.Last3Months);
    const [tcPerformanceFromDate, setTcPerformanceFromDate] = useState(null);
    const [tcPerformanceToDate, setTcPerformanceToDate] = useState(null);
    const [tcPerformanceShowInActiveTeams, setTcPerformanceShowInActiveTeams] = useState(false);
    const [tcPerformanceTab, setTcPerformanceTab] = useState(1);

    const [andNextCensusTab, setAndNextCensusTab] = useState(1);

    const [onboardingProviderId, setOnboardingProviderId] = useState('');
    const [onboardingProgramType, setOnboardingProgramType] = useState(ProgramTypeEnum.All);
    const [onboardingCardType, setOnboardingCardType] = useState(OnboardingDashboardCardType.MonthlyGoal);
    const now = new Date();
    const [onboardingSelectedYear, setOnboardingSelectedYear] = useState(now.getFullYear());
    const [onboardingSelectedMonth, setOnboardingSelectedMonth] = useState(now.getMonth() + 1);

    const [sncTeamId, setSncTeamId] = useState(authData.getDefaultTeamId('All'));
    const [sncCompareDate, setSncCompareDate] = useState<Date | null>(getPreviousMonthSameDate);
    const [sncDrillDownType, setSncDrillDownType] = useState(SNCDashboardDrillDownType.Completed);
    const [sncCompletedDateRange, setSncCompletedDateRange] = useState<SNCDashboardMetricDateRange>(SNCDashboardMetricDateRange.Today);
    const [sncUpdatedDateRange, setSncUpdatedDateRange] = useState<SNCDashboardMetricDateRange>(SNCDashboardMetricDateRange.Today);
    const [sncMetricDateRange, setSncMetricDateRange] = useState<SNCDashboardMetricDateRange>(SNCDashboardMetricDateRange.Today);
    const [sncStatus, setSncStatus] = useState<string | undefined>(undefined);
    const [sncTeamOptions, setSncTeamOptions] = useState<ISNCDashboardTeam[]>([]);
    const [sncProviderId, setSncProviderId] = useState<string>('All');
    const [sncProviderOptions, setSncProviderOptions] = useState<ISNCDashboardProvider[]>([]);

    const updateAuthData = async () => {
        const updatedAuth = new Authorization();
        setAuthData(updatedAuth);
        setEngagementSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setSchedulingSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setRequestSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setAppointmentSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setTeamMemberSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setTcTeam(updatedAuth.getDefaultTeamId('All'));
        setTcPerformanceTeam(updatedAuth.getDefaultTeamId('All'));
        setEngagementSummaryTeam(updatedAuth.getDefaultTeamId('All'));
        setCarePlanSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setDocumentSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setScheduledSelectedTeam(updatedAuth.getDefaultTeamId('All'));
        setMemberWithoutEncounterFilter({
            ...memberWithoutEncounterFilter,
            team: updatedAuth.getDefaultTeamId('All'),
        });
        setSncTeamId(updatedAuth.getDefaultTeamId('All'));
    };

    const value = {
        updateAuthData,
        selectedProvider,
        setSelectedProvider,
        showInActiveProviders,
        setShowInActiveProviders,

        engagementSelectedPlans,
        setEngagementSelectedPlans,
        engagementDateFilter,
        setEngagementDateFilter,
        engagementFromDate,
        setEngagementFromDate,
        engagementToDate,
        setEngagementToDate,
        engagementSelectedTeam,
        setEngagementSelectedTeam,
        engagementShowInActiveTeams,
        setEngagementShowInActiveTeams,

        schedulingDateFilter,
        setSchedulingDateFilter,
        schedulingFromDate,
        setSchedulingFromDate,
        schedulingToDate,
        setSchedulingToDate,
        schedulingSelectedPlans,
        setSchedulingSelectedPlans,
        schedulingSelectedProvider,
        setSchedulingSelectedProvider,
        schedulingSelectedStaff,
        setSchedulingSelectedStaff,
        schedulingSelectedTeam,
        setSchedulingSelectedTeam,
        loadedSchedulingTeam,
        setLoadedSchedulingTeam,
        schedulingShowInActiveProviders,
        setSchedulingShowInActiveProviders,
        schedulingShowInActiveTeams,
        setSchedulingShowInActiveTeams,
        schedulingShowInActiveStaffs,
        setSchedulingShowInActiveStaffs,

        requestSelectedPlan,
        setRequestSelectedPlan,
        requestSelectedProvider,
        setRequestSelectedProvider,
        requestSelectedTeam,
        setRequestSelectedTeam,
        selectedRequestType,
        setSelectedRequestType,
        loadedRequestTeam,
        setLoadedRequestTeam,
        loadedEngagementTeam,
        setLoadedEngagementTeam,
        appointmentSelectedProvider,
        setAppointmentSelectedProvider,
        appointmentSelectedTeam,
        setAppointmentSelectedTeam,
        appointmentDateFilter,
        setAppointmentDateFilter,
        appointmentFromDate,
        setAppointmentFromDate,
        appointmentToDate,
        setAppointmentToDate,
        loadedAppointmentTeam,
        setLoadedAppointmentTeam,
        appointmentShowInActiveProviders,
        setAppointmentShowInActiveProviders,
        appointmentShowInActiveTeams,
        setAppointmentShowInActiveTeams,

        teamMemberSelectedPlans,
        setTeamMemberSelectedPlans,
        teamMemberSelectedTeam,
        setTeamMemberSelectedTeam,
        loadedTeamMemberTeam,
        setLoadedTeamMemberTeam,
        teamShowInActiveTeams,
        setTeamShowInActiveTeams,
        teamMemberFromDate,
        setTeamMemberFromDate,
        teamMemberToDate,
        setTeamMemberToDate,

        reqSelectedProvider,
        setReqSelectedProvider,
        reqDayFilter,
        setReqDayFilter,
        reqDateFilter,
        setReqDateFilter,
        reqFromDate,
        setReqFromDate,
        reqToDate,
        setReqToDate,
        loadedReqProvider,
        setLoadedReqProvider,
        requestShowInActiveProviders,
        setRequestShowInActiveProviders,

        tcTeam,
        setTcTeam,
        tcFacility,
        setTcFacility,
        tcAdmissionRules,
        setTcAdmissionRules,
        tcDateFilter,
        setTcDateFilter,
        tcFromDate,
        setTcFromDate,
        tcToDate,
        setTcToDate,
        tcShowInActiveTeams,
        setTcShowInActiveTeams,
        tcTab,
        setTcTab,

        engagementSummaryDateFilter,
        setEngagementSummaryDateFilter,
        engagementSummaryProvider,
        setEngagementSummaryProvider,
        engagementSummaryTeam,
        setEngagementSummaryTeam,
        engagementSummaryFromDate,
        setEngagementSummaryFromDate,
        engagementSummaryToDate,
        setEngagementSummaryToDate,
        engagementSummaryShowInActiveTeams,
        setEngagementSummaryShowInActiveTeams,
        engagementSummaryShowInActiveProviders,
        setEngagementSummaryShowInActiveProviders,

        memberWithoutEncounterFilter,
        setMemberWithoutEncounterFilter,

        undeterminedReferralDay,
        setUndeterminedReferralDay,
        undeterminedIncludeScheduledStatus,
        setUndeterminedIncludeScheduledStatus,

        inProgressProvider,
        setInProgressProvider,
        inProgressSelectedDay,
        setInProgressSelectedDay,
        inProgressDateFilter,
        setInProgressDateFilter,
        inProgressFromDate,
        setInProgressFromDate,
        inProgressToDate,
        setInProgressToDate,
        inProgressShowInActiveProviders,
        setInProgressShowInActiveProviders,
        closedSelectedFilter,
        setClosedSelectedFilter,
        closedDateFilter,
        setClosedDateFilter,
        closedFromDate,
        setClosedFromDate,
        closedToDate,
        setClosedToDate,

        qualityMetricsDateFilter,
        setQualityMetricsDateFilter,
        qualityMetricsFromDate,
        setQualityMetricsFromDate,
        qualityMetricsToDate,
        setQualityMetricsToDate,
        qualityMetricsTab,
        setQualityMetricsTab,

        exceptionTab,
        setExceptionTab,

        individualEngagementDateFilter,
        setIndividualEngagementDateFilter,
        individualEngagementFromDate,
        setIndividualEngagementFromDate,
        individualEngagementToDate,
        setIndividualEngagementToDate,
        individualEngagementSelectedProvider,
        setIndividualEngagementSelectedProvider,
        individualEngagementShowInActiveProviders,
        setIndividualEngagementShowInActiveProviders,

        onboardCallSelectedProvider,
        setOnboardCallSelectedProvider,
        onboardCallSelectedFilter,
        setOnboardCallSelectedFilter,
        onboardCallFromDate,
        setOnboardCallFromDate,
        onboardCallToDate,
        setOnboardCallToDate,
        onboardCallShowInActiveProviders,
        setOnboardCallShowInActiveProviders,
        onboardCallPopulationTypes,
        setOnboardCallPopulationTypes,

        fuhfumVisitReadmissionDateFilter,
        setFuhfumVisitReadmissionDateFilter,
        fuhfumVisitReadmissionFromDate,
        setFuhfumVisitReadmissionFromDate,
        fuhfumVisitReadmissionToDate,
        setFuhfumVisitReadmissionToDate,
        fuhfumVisitReadmissionTab,
        setFuhfumVisitReadmissionTab,

        appointmentTrackingTab,
        setAppointmentTrackingTab,
        fullProgramAppointment,
        setFullProgramAppointment,
        hraAppointment,
        setHraAppointment,
        fuhfumAppointment,
        setFuhfumAppointment,

        fullProgramReferralDateFilter,
        setFullProgramReferralDateFilter,
        fullProgramReferralFromDate,
        setFullProgramReferralFromDate,
        fullProgramReferralToDate,
        setFullProgramReferralToDate,
        fullProgramReferralTab,
        setFullProgramReferralTab,

        scheduledFilterBy,
        setScheduledFilterBy,
        scheduledSelectedTeam,
        setScheduledSelectedTeam,
        scheduledShowInActiveTeams,
        setScheduledShowInActiveTeams,
        scheduledFromDate,
        setScheduledFromDate,
        scheduledToDate,
        setScheduledToDate,

        submittedClaimSelectedProvider,
        setSubmittedClaimSelectedProvider,
        submittedClaimSelectedPlans,
        setSubmittedClaimSelectedPlans,
        submittedClaimSelectedTeam,
        setSubmittedClaimSelectedTeam,
        submittedClaimShowInActiveTeams,
        setSubmittedClaimShowInActiveTeams,
        submittedClaimShowInActiveProviders,
        setSubmittedClaimShowInActiveProviders,

        inpersonVisitProvider,
        setInpersonVisitProvider,
        inpersonVisitYear,
        setInpersonVisitYear,
        inpersonVisitMonth,
        setInpersonVisitMonth,
        inpersonVisitShowInActiveProviders,
        setInpersonVisitShowInActiveProviders,

        surveySelectedProvider,
        setSurveySelectedProvider,
        surveySelectedFilter,
        setSurveySelectedFilter,
        surveyFromDate,
        setSurveyFromDate,
        surveyToDate,
        setSurveyToDate,
        surveyShowInActiveProviders,
        setSurveyShowInActiveProviders,

        schedulerSelectedProvider,
        setSchedulerSelectedProvider,
        schedulerDateFilter,
        setSchedulerDateFilter,
        schedulerFromDate,
        setSchedulerFromDate,
        schedulerToDate,
        setSchedulerToDate,
        schedulerShowInActiveProviders,
        setSchedulerShowInActiveProviders,

        carePlanSelectedTeam,
        setCarePlanSelectedTeam,
        carePlanDateFilter,
        setCarePlanDateFilter,
        carePlanDate,
        setCarePlanDate,
        carePlanShowInActiveTeams,
        setCarePlanShowInActiveTeams,

        memberPromisesSelectedProvider,
        setMemberPromisesSelectedProvider,
        memberPromisesShowInActiveProviders,
        setMemberPromisesShowInActiveProviders,
        memberPromisesSelectedTeam,
        setMemberPromisesSelectedTeam,
        memberPromisesShowInActiveTeams,
        setMemberPromisesShowInActiveTeams,
        memberPromisesRequestType,
        setMemberPromisesRequestType,
        memberPromisesFilterBy,
        setMemberPromisesFilterBy,
        memberPromisesTab,
        setMemberPromisesTab,
        memberPromisesFromDate,
        setMemberPromisesFromDate,
        memberPromisesToDate,
        setMemberPromisesToDate,

        documentTimePeriod,
        setDocumentTimePeriod,
        documentSelectedTeam,
        setDocumentSelectedTeam,
        documentShowInActiveTeams,
        setDocumentShowInActiveTeams,

        tcPerformanceTeam,
        setTcPerformanceTeam,
        tcPerformanceFilterRole,
        setTcPerformanceFilterRole,
        tcPerformanceDateFilter,
        setTcPerformanceDateFilter,
        tcPerformanceFromDate,
        setTcPerformanceFromDate,
        tcPerformanceToDate,
        setTcPerformanceToDate,
        tcPerformanceShowInActiveTeams,
        setTcPerformanceShowInActiveTeams,
        tcPerformanceTab,
        setTcPerformanceTab,

        andNextCensusTab,
        setAndNextCensusTab,

        onboardingProviderId,
        setOnboardingProviderId,
        onboardingProgramType,
        setOnboardingProgramType,
        onboardingCardType,
        setOnboardingCardType,
        onboardingSelectedYear,
        setOnboardingSelectedYear,
        onboardingSelectedMonth,
        setOnboardingSelectedMonth,

        sncTeamId,
        setSncTeamId,
        sncCompareDate,
        setSncCompareDate,
        sncDrillDownType,
        setSncDrillDownType,
        sncCompletedDateRange,
        setSncCompletedDateRange,
        sncUpdatedDateRange,
        setSncUpdatedDateRange,
        sncMetricDateRange,
        setSncMetricDateRange,
        sncStatus,
        setSncStatus,
        sncTeamOptions,
        setSncTeamOptions,
        sncProviderId,
        setSncProviderId,
        sncProviderOptions,
        setSncProviderOptions,
    };

    return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
};

export const useWidgetContext = () => React.useContext(WidgetContext);
